import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { faExchange, faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Constraint, Container, Dropdown, FlexGrid, Form, Header, Input, Label, Link, Loader, LoadingMask, MapBoxMap, Message, Panel, Pill, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useBannerAds from '../../../lib/hooks/useBannerAds';
import useConfig from '../../../lib/hooks/useConfig';
import useCreateVehicleMutation, { GET_VEHICLES } from '../../../lib/hooks/useCreateVehicleMutation';
import useProfile from '../../../lib/hooks/useProfile';
import useVehicleForm from '../../../lib/hooks/useVehicleForm';
import { GET_TRIMS, useVehicleTrimOptions } from '../../../lib/hooks/useVehicleTrims';
import { flattenReducer } from '../../../lib/utils';
import { AD_SLOTS, ANALYTICS_EVENTS, CURRENCY_CODES, CUSTOM_VEHICLE_MAKE_ID, CUSTOM_VEHICLE_MODEL_ID, FUEL_OPTIONS } from '../../constants';
import PriceUnits from '../../constants/priceUnits';
import { Ad } from '../Ad';
import AdContainer from '../Ad/AdContainer';
import CityAutocomplete from '../CityAutocomplete';
import Page from '../Page';
import PageTakeover from '../PageTakeover';
import styles from './TripCostCalculatorPage.module.css';

const cx = classnames.bind(styles);
const desktopBannerName = 'GasBuddy_TCC1_728x90';
const mobileBannerName = 'GasBuddy_TCC1_320x50_Search';
const PAGE_NAME = 'Trip_Cost_Calculator';
const MAX_DESTINATIONS = 10;
const padArray = (arr, minItems, padding = '') => (arr.length < minItems ? Array.from(Array(minItems), (v, i) => arr[i] || padding) : arr);
const swapFuelEfficiency = prevEfficiency => (235.215 / parseFloat(prevEfficiency, 10)).toFixed(2);

export const GET_EFFICIENCY = gql`
  query getVehicleEfficiency($modelId: Int!, $year: Int!, $trimId: Int!) {
    vehicleEfficiency(modelId: $modelId, year: $year, trimId: $trimId) {
      cityEfficiency
      highwayEfficiency
      recommendedFuelId
      tankSize
    }
  }
`;

export const GET_TRIP = gql`
  query getTrip(
    $cityEfficiency: Float!,
    $fuelType: Int!,
    $highwayEfficiency: Float!,
    $metric: Boolean!,
    $roundTrip: Boolean!,
    $tankSize: Float!
    $waypoints: [String!]!,
  ) {
    trip(
      cityEfficiency: $cityEfficiency,
      fuelType: $fuelType,
      highwayEfficiency: $highwayEfficiency,
      metric: $metric,
      roundTrip: $roundTrip,
      tankSize: $tankSize,
      waypoints: $waypoints,
    ) {
      countryCrossed
      distance
      fuelQuantity
      inboundTripLegs {
        endWaypoint {
          displayName
          label
          latitude
          longitude
        }
        fillups {
          area {
            latitude
            longitude
            name
            price
            priceUnit
          }
          currency
          fillupNumber
          fuelCost
          fuelQuantity
          station {
            address {
              country
              line1
              line2
              locality
              region
            }
            latitude
            longitude
            name
            priceUnit
          }
          stationFuelPrice
        }
        startWaypoint {
          displayName
          label
          latitude
          longitude
        }
      }
      isMetric
      outboundTripLegs {
        endWaypoint {
          displayName
          label
          latitude
          longitude
        }
        fillups {
          area {
            latitude
            longitude
            name
            price
            priceUnit
          }
          currency
          fillupNumber
          fuelCost
          fuelQuantity
          station {
            address {
              country
              line1
              line2
              locality
              region
            }
            latitude
            longitude
            name
            priceUnit
          }
          stationFuelPrice
        }
        startWaypoint {
          displayName
          label
          latitude
          longitude
        }
      }
      roundTrip
      routeGeoCoords
      savings
      totalCost
    }
  }
`;

export default function TripCostCalculatorPage({ origin: defaultOrigin, destinations: defaultDestinations }) {
  const [fuel, setFuel] = useState(FUEL_OPTIONS[0].value.toString());
  const [origin, setOrigin] = useState(defaultOrigin || '');
  const [destinations, setDestinations] = useState(defaultDestinations || ['', '']);
  const [isRoundTrip, setIsRoundTrip] = useState(true);
  const [isMetric, setIsMetric] = useState(false);
  const [cityEfficiency, setCityEfficiency] = useState('');
  const [highwayEfficiency, setHighwayEfficiency] = useState('');
  const [tankSize, setTankSize] = useState('');
  const [shouldSaveVehicle, setShouldSaveVehicle] = useState(false);
  const [hasSavedVehicle, setHasSavedVehicle] = useState(false);
  const [displayError, setDisplayError] = useState();
  const [displayWarning, setDisplayWarning] = useState();
  const { t } = useTranslation(['trip', 'vehicle', 'fuel']);
  const map = useRef();
  const analytics = useAnalytics();

  const fuelOptions = useMemo(() => FUEL_OPTIONS.map(option => ({
    ...option,
    label: t(`${option.label.toLowerCase()}Fuel`, { ns: 'fuel' }),
  })), [t]);

  const [fetchTrip, { data, error: tripError, loading }] = useLazyQuery(GET_TRIP, {
    onCompleted: () => {
      setDisplayError('');
      setDisplayWarning('');
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_TRIP_CALCULATION_COMPLETED,
      });
    },
  });

  const [createVehicleProfile, {
    data: vehicleCreationResult,
    loading: isSavingVehicle,
    error: vehicleCreationError,
  }] = useCreateVehicleMutation({
    onCompleted: () => {
      setShouldSaveVehicle(false);
      setHasSavedVehicle(true);
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.VEHICLE_ADDED,
        attributes: {
          'Screen Name': PAGE_NAME,
        },
      });
    },
  });

  const scrollToMap = useCallback(() => {
    if (map.current) {
      map.current.scrollIntoView();
    }
  }, []);

  const tripDetails = data?.trip;
  const bannerAds = useBannerAds(desktopBannerName, mobileBannerName);
  const { isLoggedIn } = useProfile();
  const { mapboxMapKey: mapKey } = useConfig();
  const {
    error: vehicleFormError,
    loading: loadingVehicleDetail,
    yearOptions,
    makeOptions,
    modelOptions,
    trimOptions,
    handleYear,
    handleMake,
    handleModel,
    handleTrim,
    make,
    model,
    trim,
    year,
  } = useVehicleForm('database');
  const [vehicleGuid, setVehicleGuid] = useState();
  const { data: vehiclesData } = useQuery(GET_VEHICLES, { skip: !isLoggedIn });
  const hasVehicles = isLoggedIn && vehiclesData?.myVehicles?.length;
  const waypointsProvided = origin && destinations.length;
  const vehicleDetailsProvided = cityEfficiency && highwayEfficiency && tankSize && fuel;
  const canSubmit = !loading && waypointsProvided && vehicleDetailsProvided;

  const myVehicles = useMemo(() => vehiclesData?.myVehicles.map(v => ({ label: v.nickname || `${v.year} ${v.make} ${v.model}`, value: v.guid })), [vehiclesData]);
  const selectedVehicle = useMemo(() => vehiclesData?.myVehicles.find(v => v.guid === vehicleGuid), [vehicleGuid, vehiclesData]);
  const [isSelectingNewVehicle, setIsSelectingNewVehicle] = useState(isLoggedIn ? myVehicles?.length === 0 : true);

  const { data: myVehicleTrims, loading: isLoadingMyVehicleTrims, error: myVehicleTrimError } = useQuery(GET_TRIMS, {
    skip: !selectedVehicle || selectedVehicle?.makeId === CUSTOM_VEHICLE_MAKE_ID || selectedVehicle?.modelId === CUSTOM_VEHICLE_MODEL_ID,
    variables: {
      modelId: selectedVehicle?.modelId,
      year: selectedVehicle?.year,
    },
  });
  const myVehicleTrimOptions = useVehicleTrimOptions(myVehicleTrims?.vehicleTrims);
  const vehicleTrimOptions = useMemo(() => {
    if (!isSelectingNewVehicle) {
      return selectedVehicle ? myVehicleTrimOptions : [];
    }

    return model ? trimOptions : [];
  }, [isSelectingNewVehicle, model, myVehicleTrimOptions, selectedVehicle, trimOptions]);

  const clearVehicleInfo = useCallback(() => {
    setDisplayError('');
    setCityEfficiency('');
    setHighwayEfficiency('');
    setTankSize('');
    setVehicleGuid('');
  }, []);

  const setMyVehicle = useCallback(() => {
    setIsSelectingNewVehicle(false);
    setShouldSaveVehicle(false);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_MY_VEHICLE_CLICKED,
    });
    clearVehicleInfo();
  }, [analytics, clearVehicleInfo]);

  const setNewVehicle = useCallback(() => {
    setIsSelectingNewVehicle(true);
    setShouldSaveVehicle(false);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_NEW_VEHICLE_CLICKED,
    });
    clearVehicleInfo();
  }, [analytics, clearVehicleInfo]);

  const isInVehicleSelectMode = useMemo(() => hasVehicles && !isSelectingNewVehicle, [hasVehicles, isSelectingNewVehicle]);
  const { data: efficiencyData, loading: isLoadingEfficiencyData, error: myVehicleEfficiencyError } = useQuery(GET_EFFICIENCY, {
    // TODO: must be no-cache until we update Apollo Client. Any other policy prevents data, loading and error from
    // updating when the query completes, despite the query finishing. This happens even with useLazyQuery.
    // https://github.com/apollographql/apollo-client/issues/6334#issuecomment-1113998616
    fetchPolicy: 'no-cache',
    skip: isInVehicleSelectMode
      ? !trim
      : !make || !model || !trim || !year,
    variables: {
      modelId: isInVehicleSelectMode ? selectedVehicle?.modelId : parseInt(model, 10),
      trimId: parseInt(trim, 10),
      year: isInVehicleSelectMode ? selectedVehicle?.year : parseInt(year, 10),
    },
  });

  useEffect(() => {
    if (efficiencyData) {
      setCityEfficiency(isMetric
        ? swapFuelEfficiency(efficiencyData.vehicleEfficiency.cityEfficiency)
        : efficiencyData.vehicleEfficiency.cityEfficiency);
      setHighwayEfficiency(isMetric
        ? swapFuelEfficiency(efficiencyData.vehicleEfficiency.highwayEfficiency)
        : efficiencyData.vehicleEfficiency.highwayEfficiency);
      setTankSize(isMetric
        ? (parseFloat(efficiencyData.vehicleEfficiency.tankSize) * 3.785).toFixed(1)
        : efficiencyData.vehicleEfficiency.tankSize);
      setFuel(efficiencyData.vehicleEfficiency.recommendedFuelId);
    }
  }, [efficiencyData, isMetric]);

  const handleVehicle = useCallback((e) => {
    setVehicleGuid(e.target.value);
    if (e.target.value) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_MY_VEHICLE_CHANGED,
      });
    }
  }, [analytics]);

  const handleYearChange = useCallback((e) => {
    handleYear(e);
    if (e.target.value) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_VEHICLE_FORM_YEAR_CHANGED,
      });
    }
  }, [analytics, handleYear]);

  const handleMakeChange = useCallback((e) => {
    handleMake(e);
    if (e.target.value) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_VEHICLE_FORM_MAKE_CHANGED,
      });
    }
  }, [analytics, handleMake]);

  const handleModelChange = useCallback((e) => {
    handleModel(e);
    if (e.target.value) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_VEHICLE_FORM_MODEL_CHANGED,
      });
    }
  }, [analytics, handleModel]);

  const handleTrimChange = useCallback((e) => {
    handleTrim(e);
    if (e.target.value) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_VEHICLE_FORM_TRIM_CHANGED,
      });
    }
  }, [analytics, handleTrim]);

  const handleFuelChange = useCallback((e) => {
    setFuel((prevFuel) => {
      if (prevFuel && prevFuel !== e.target.value) {
        analytics.tagEvent({
          name: ANALYTICS_EVENTS.TCC_FUEL_CHANGED,
        });
      }
      return e.target.value;
    });
  }, [analytics]);

  const handleCityEfficiency = useCallback((e) => {
    setCityEfficiency(e.target.value);
  }, []);

  const handleHighwayEfficiency = useCallback((e) => {
    setHighwayEfficiency(e.target.value);
  }, []);

  const handleTankSize = useCallback((e) => {
    setTankSize(e.target.value);
  }, []);

  const handleSaveVehicleChange = useCallback((e) => {
    setShouldSaveVehicle(e.target.checked);
  }, []);

  const destinationChangeHandlers = useMemo(() => padArray(destinations, 2).map((d, i) => (searchTerm) => {
    setDestinations((currentDestinations) => {
      const newDestinations = [...currentDestinations];
      newDestinations[i] = searchTerm;
      return newDestinations;
    });
  }), [destinations]);

  const destinationBlurHandlers = useMemo(() => padArray(destinations, 2).map((d, i) => () => {
    if (destinations[i]?.length > 0) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_FILLED_DESTINATION_BLURRED,
        attributes: {
          'Location Type': i === 0 ? 'Destination' : `Intermediate Waypoint ${i}`,
        },
      });
    }
  }), [analytics, destinations]);

  const destinationRemovalHandlers = useMemo(() => destinations.slice(1).map((d, i) => () => {
    setDestinations((currentDestinations) => {
      const newDestinations = [...currentDestinations];
      newDestinations.splice(i, 1);
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_WAYPOINT_REMOVED,
      });
      return newDestinations;
    });
  }), [analytics, destinations]);

  const addDestination = useCallback(() => {
    setDestinations(currentDestinations => [...currentDestinations, '']);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_ADD_DESTINATION_CLICKED,
    });
  }, [analytics]);

  const swapOriginWithDestination = useCallback(() => {
    setOrigin(destinations[0]);
    setDestinations([origin, ...destinations.slice(1)]);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_SWAP_ORIGIN_CLICKED,
    });
  }, [analytics, destinations, origin]);

  const handleSubmitTrip = useCallback((e) => {
    e.preventDefault();
    // reset the saved vehicle trigger in case the new trip isn't saving a vehicle anymore
    setHasSavedVehicle(false);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_CALCULATE_TRIP_CLICKED,
    });

    fetchTrip({
      variables: {
        cityEfficiency: parseFloat(cityEfficiency),
        fuelType: parseInt(fuel, 10),
        highwayEfficiency: parseFloat(highwayEfficiency),
        metric: isMetric,
        roundTrip: isRoundTrip,
        tankSize: parseFloat(tankSize),
        waypoints: [origin, ...destinations.filter((destination, idx) => idx !== 0 && !!destination), destinations[0]],
        // ^^ first destination in list is the trip endpoint
      },
    });

    // Check if we need to save the vehicle
    if (!isInVehicleSelectMode && shouldSaveVehicle) {
      createVehicleProfile({
        variables: {
          vehicle: {
            year: parseInt(year, 10),
            make: makeOptions.find(mo => mo.value === make)?.label,
            makeId: parseInt(make, 10),
            model: modelOptions.find(mo => mo.value === model)?.label,
            modelId: parseInt(model, 10),
          },
        },
      });
    }
  }, [
    analytics,
    cityEfficiency,
    createVehicleProfile,
    destinations,
    fetchTrip,
    fuel,
    highwayEfficiency,
    isInVehicleSelectMode,
    isMetric,
    isRoundTrip,
    make,
    makeOptions,
    model,
    modelOptions,
    origin,
    shouldSaveVehicle,
    tankSize,
    year,
  ]);

  const handleOriginChange = useCallback(searchTerm => setOrigin(searchTerm), []);

  const handleOriginBlur = useCallback(() => {
    if (origin.length > 0) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.TCC_FILLED_ORIGIN_BLURRED,
      });
    }
  }, [analytics, origin]);

  const setRoundTrip = useCallback(() => {
    setIsRoundTrip(true);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_ROUND_TRIP_CLICKED,
    });
  }, [analytics]);

  const setOneWay = useCallback(() => {
    setIsRoundTrip(false);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_ONE_WAY_CLICKED,
    });
  }, [analytics]);

  const setNonMetric = useCallback(() => {
    setIsMetric(false);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_NON_METRIC_CLICKED,
    });
  }, [analytics]);

  const setMetric = useCallback(() => {
    setIsMetric(true);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.TCC_METRIC_CLICKED,
    });
  }, [analytics]);

  const routeMarkers = useMemo(() => {
    if (!tripDetails?.outboundTripLegs?.length) {
      return [];
    }

    let markers = [
      <MapBoxMap.Marker
        key={`marker${tripDetails.outboundTripLegs[0].startWaypoint.label}`}
        className={cx('marker', 'waypointMarker')}
        label={tripDetails.outboundTripLegs[0].startWaypoint.label}
        lat={tripDetails.outboundTripLegs[0].startWaypoint.latitude}
        lng={tripDetails.outboundTripLegs[0].startWaypoint.longitude}
      />,
    ];

    markers = markers.concat(tripDetails.outboundTripLegs.map(leg => leg.fillups.reduce((stops, currStop) => (
      // Markers for stations along route for leg
      stops.concat([
        <MapBoxMap.Marker
          key={`marker${currStop.fillupNumber}`}
          className={cx('marker')}
          label={currStop.fillupNumber.toString()}
          lat={(currStop.station || currStop.area).latitude}
          lng={(currStop.station || currStop.area).longitude}
        />,
      ])
    ), []).concat([
      // Marker for leg endpoint
      <MapBoxMap.Marker
        key={`marker${leg.endWaypoint.label}`}
        className={cx('marker', 'waypointMarker')}
        label={leg.endWaypoint.label}
        lat={leg.endWaypoint.latitude}
        lng={leg.endWaypoint.longitude}
      />,
    ])).reduce(flattenReducer, []));

    if (tripDetails.inboundTripLegs.length > 0) {
      // Note: We already have the markers for our leg endpoints, so no need to double up here
      markers = markers.concat(tripDetails.inboundTripLegs.map(leg => leg.fillups.reduce((stops, currStop) => (
        stops.concat([
          <MapBoxMap.Marker
            key={`marker${currStop.fillupNumber}`}
            className={cx('marker')}
            label={currStop.fillupNumber.toString()}
            lat={(currStop.station || currStop.area).latitude}
            lng={(currStop.station || currStop.area).longitude}
          />,
        ])
      ), [])).reduce(flattenReducer, []));
    }

    return markers;
  }, [tripDetails]);

  const generateFillup = (stop) => {
    const stopFuelUnit = (stop.station || stop.area).priceUnit === PriceUnits.DollarsPerGallon ? 'gallon' : 'litre';
    return (
      <Panel className={cx('fillup')} color="white" key={`fillup${stop.fillupNumber}`} raised padded={false}>
        <FlexGrid className={cx('fillupGrid')}>
          <FlexGrid.Column mobile={12} desktop={5}>
            <FlexGrid>
              <FlexGrid.Column mobile={2}>
                <div className={cx('stepNumber')}>{stop.fillupNumber}</div>
              </FlexGrid.Column>
              <FlexGrid.Column className={cx('fillAddress')} mobile={10}>
                {stop.station ? (
                  <Fragment>
                    <Text fluid>{stop.station.name}</Text>
                    <Text fluid>{stop.station.address.line1}</Text>
                    <Text fluid>{stop.station.address.locality}, {stop.station.address.region}</Text>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Text fluid>{stop.area.name}</Text>
                    <Text fluid>{t('areaAverage', { ns: 'trip' })}</Text>
                  </Fragment>
                )}
              </FlexGrid.Column>
            </FlexGrid>
          </FlexGrid.Column>
          <FlexGrid.Column mobile={12} desktop={7}>
            <FlexGrid className={cx('fillInformation')}>
              <FlexGrid.Column className={cx('metric')} mobile={4}>
                <Text bold fluid className={cx('fillValue')}>
                  {stop.station ? stop.stationFuelPrice : stop.area.price}
                </Text>
                <Text fluid size="sm">
                  {t(`pricePer_${stopFuelUnit}`, { ns: 'trip' })}
                </Text>
              </FlexGrid.Column>
              <FlexGrid.Column className={cx('metric')} mobile={4}>
                <Text bold fluid className={cx('fillValue')}>{stop.fuelQuantity}</Text>
                <Text fluid size="sm">
                  {t(`fillup_${stopFuelUnit}`, { ns: 'trip' })}
                </Text>
              </FlexGrid.Column>
              <FlexGrid.Column className={cx('metric')} mobile={4}>
                <Text bold color="blue" fluid className={cx('fillValue')}>{stop.fuelCost}</Text>
                <Text fluid size="sm">
                  {t('amountCurrency', {
                    ns: 'trip',
                    currency: tripDetails.countryCrossed ? ` (${stop.currency})` : '',
                  })}
                </Text>
              </FlexGrid.Column>
            </FlexGrid>
          </FlexGrid.Column>
        </FlexGrid>
      </Panel>
    );
  };

  const generateLegDetails = (leg, useSmallerHeader) => (
    <div
      className={cx('legContainer')}
      key={`${leg.startWaypoint.displayName}_${leg.endWaypoint.displayName}`}
    >
      <Header as={useSmallerHeader ? 'h4' : 'h3'} className={cx('waypointHeader')} size="sm">
        <div className={cx('legWaypoint')}>
          <div className={cx('waypointLabel')}>{leg.startWaypoint.label}</div>
          <Text className={cx('waypointName')}>
            &nbsp;{leg.startWaypoint.displayName}&nbsp;&gt;&nbsp;
          </Text>
        </div>
        <div className={cx('legWaypoint')}>
          <div className={cx('waypointLabel')}>{leg.endWaypoint.label}</div>
          <Text className={cx('waypointName')}>
            &nbsp;{leg.endWaypoint.displayName}
          </Text>
        </div>
      </Header>
      {leg.fillups.map(stop => generateFillup(stop))}
    </div>
  );

  const summaryCurrency = useMemo(() => {
    if (!tripDetails?.countryCrossed) {
      return '';
    }
    return ` (${tripDetails.isMetric ? CURRENCY_CODES.CANADA : CURRENCY_CODES.USA})`;
  }, [tripDetails]);

  const vehicleFormLoadingMessage = useMemo(() => {
    if (loadingVehicleDetail) {
      return t(`loading_${loadingVehicleDetail}`, { ns: 'vehicle' });
    }
    if (isLoadingMyVehicleTrims) {
      return t('loading_trims', { ns: 'vehicle' });
    }
    if (isLoadingEfficiencyData) {
      return t('loadingEfficiency', { ns: 'vehicle' });
    }
    return '';
  }, [isLoadingEfficiencyData, isLoadingMyVehicleTrims, loadingVehicleDetail, t]);

  useEffect(() => {
    let error;
    if (tripError) {
      error = t('errorCalculatingTrip', { ns: 'trip' });
    }
    if (vehicleFormError) {
      if (vehicleFormError.message === 'Trim details not found') {
        error = t('cantFindInfo', { ns: 'vehicle' });
      } else {
        error = t('vehicleInfoFetchError', { ns: 'vehicle' });
      }
    }
    if (myVehicleTrimError) {
      if (myVehicleTrimError.message === 'Trim details not found') {
        error = t('cantFindTrims', { ns: 'vehicle' });
      } else {
        error = t('vehicleTrimFetchError', { ns: 'vehicle' });
      }
    }
    if (myVehicleEfficiencyError) {
      error = t('vehicleEfficiencyFetchError', { ns: 'vehicle' });
    }

    setDisplayError(error);
  }, [myVehicleEfficiencyError, myVehicleTrimError, t, tripError, vehicleFormError]);

  useEffect(() => {
    let warning;
    const currCityEfficiency = efficiencyData?.vehicleEfficiency?.cityEfficiency;
    const currHighwayEfficiency = efficiencyData?.vehicleEfficiency?.highwayEfficiency;
    if (currCityEfficiency > 0 && currHighwayEfficiency > 0 && currCityEfficiency === currHighwayEfficiency) {
      warning = t('combinedFuelEfficiencyWarning', { ns: 'trip' });
    }

    if (selectedVehicle?.makeId === CUSTOM_VEHICLE_MAKE_ID || selectedVehicle?.modelId === CUSTOM_VEHICLE_MODEL_ID) {
      warning = t('manualFuelEfficiencyWarning', { ns: 'trip' });
    }
    setDisplayWarning(warning);
  }, [efficiencyData, selectedVehicle, t]);

  useEffect(() => {
    if (map.current) {
      map.current.scrollIntoView();
    }
  }, []);

  return (
    <Page
      description={t('pageDescription', { ns: 'trip' })}
      name={PAGE_NAME}
      title={t('pageTitle', { ns: 'trip' })}
    >
      <PageTakeover adUnitPath="/1025479/GasBuddy_TCC_Takeover" />
      <Container className={cx('mainContainer')}>
        <Header as="h1" uppercase>
          {t('mainHeader', { ns: 'trip' })}
        </Header>
        <Form onSubmit={handleSubmitTrip} role="form">
          <FlexGrid container>
            <FlexGrid.Column tablet={7} desktop={6} className={cx('tripInformation')}>
              <Panel className={cx('blueBox')} color="blueLight">
                <Constraint mobile={11} desktop={10} desktopOffset={1}>
                  <Header as="h2" className={cx('blueBoxHeader')} size="sm">
                    {t('subHeader', { ns: 'trip' })}
                  </Header>
                  <Text as="p">
                    {t('formIntro', { ns: 'trip' })}
                  </Text>
                  <br />
                  <Text as="p" bold centered size="sm">
                    {t('selectTripType', { ns: 'trip' })}
                  </Text>
                  <div className={cx('pillGroup')}>
                    <Pill
                      active={isRoundTrip}
                      className={cx('pill', { active: isRoundTrip })}
                      color="blue"
                      label={t('roundTrip', { ns: 'trip' })}
                      onClick={setRoundTrip}
                    />
                    <Pill
                      active={!isRoundTrip}
                      className={cx('pill', { active: !isRoundTrip })}
                      color="blue"
                      label={t('oneWayTrip', { ns: 'trip' })}
                      onClick={setOneWay}
                    />
                  </div>
                  <Text as="p" bold centered size="sm">Measurements</Text>
                  <div className={cx('pillGroup')}>
                    <Pill
                      active={!isMetric}
                      className={cx('pill', { active: !isMetric })}
                      color="blue"
                      label="US"
                      onClick={setNonMetric}
                    />
                    <Pill
                      active={isMetric}
                      className={cx('pill', { active: isMetric })}
                      color="blue"
                      label={t('metric', { ns: 'trip' })}
                      onClick={setMetric}
                    />
                  </div>
                  <br />
                </Constraint>
                <Constraint className={cx('routeFields')} mobile={11} desktop={10} desktopOffset={1}>
                  <CityAutocomplete
                    forceOpenIfLoading
                    id="origin"
                    label={t('origin', { ns: 'trip' })}
                    onBlur={handleOriginBlur}
                    onChange={handleOriginChange}
                    required
                    type="text"
                    value={origin}
                  />
                  <FontAwesomeIcon
                    className={cx('swapIcon')}
                    icon={faExchange}
                    onClick={swapOriginWithDestination}
                    size="2x"
                    title={t('swapOriginDestination', { ns: 'trip' })}
                  />
                  <CityAutocomplete
                    forceOpenIfLoading
                    id="destination"
                    label={t('destination', { ns: 'trip' })}
                    onBlur={destinationBlurHandlers[0]}
                    onChange={destinationChangeHandlers[0]}
                    required
                    type="text"
                    value={destinations[0]}
                  />
                </Constraint>
                {padArray(destinations, 2).slice(1).map((dest, i) => (
                  <Constraint key={`waypoint${i + 1}`} className={cx('routeFields')} mobile={11} desktop={10} desktopOffset={1}>
                    <CityAutocomplete
                      forceOpenIfLoading
                      id={`waypoint${i + 1}`}
                      label={t('addStop', { ns: 'trip' })}
                      onBlur={destinationBlurHandlers[i + 1]}
                      onChange={destinationChangeHandlers[i + 1]}
                      type="text"
                      value={dest}
                    />
                    {i < destinationRemovalHandlers.length - 1
                      ? (
                        <FontAwesomeIcon
                          className={cx('removeIcon')}
                          icon={faMinus}
                          onClick={destinationRemovalHandlers[i + 1]}
                          size="2x"
                          title={t('removeThisStop', { ns: 'trip' })}
                        />
                      )
                      : destinationRemovalHandlers.length < MAX_DESTINATIONS - 1 && (
                        <FontAwesomeIcon
                          className={cx('addIcon')}
                          icon={faPlus}
                          onClick={addDestination}
                          size="2x"
                          title={t('addAnotherStop', { ns: 'trip' })}
                        />
                      )
                    }
                  </Constraint>
                ))}
              </Panel>
            </FlexGrid.Column>
            <FlexGrid.Column tablet={5} desktop={6}>
              <Panel className={cx('blueBox')} color="blueLight">
                <LoadingMask
                  isLoading={!!vehicleFormLoadingMessage}
                  message={vehicleFormLoadingMessage}
                >
                  <Constraint centered desktop={10}>
                    <Header as="h2" className={cx('blueBoxHeader')} size="sm">Vehicle Information</Header>
                    {!!hasVehicles && (
                      <div className={cx('pillGroup')}>
                        <Pill
                          active={!isSelectingNewVehicle}
                          className={cx('pill', { active: !isSelectingNewVehicle })}
                          color="blue"
                          label={t('myVehicle', { ns: 'vehicle' })}
                          onClick={setMyVehicle}
                        />
                        <Pill
                          active={isSelectingNewVehicle}
                          className={cx('pill', { active: isSelectingNewVehicle })}
                          color="blue"
                          label={t('newVehicle', { ns: 'vehicle' })}
                          onClick={setNewVehicle}
                        />
                      </div>
                    )}
                    {!isInVehicleSelectMode ? (
                      <Fragment>
                        <Dropdown
                          name="year"
                          placeholder={t('selectYearPlaceholder', { ns: 'vehicle' })}
                          options={yearOptions}
                          onChange={handleYearChange}
                        />
                        <Dropdown
                          name="makeId"
                          placeholder={t('selectMakePlaceholder', { ns: 'vehicle' })}
                          options={makeOptions}
                          disabled={!makeOptions.length}
                          onChange={handleMakeChange}
                          searchable
                        />
                        <Dropdown
                          name="modelId"
                          placeholder={!!year && !!make && !modelOptions.length
                            ? t('noModelsFound', { ns: 'vehicle' })
                            : t('selectModelPlaceholder', { ns: 'vehicle' })
                          }
                          options={modelOptions}
                          disabled={!modelOptions.length}
                          onChange={handleModelChange}
                          searchable
                        />
                      </Fragment>
                    ) : (
                      // TODO: This Fragment is sacrilegious to our tests. Removing it breaks the withAccount tests
                      // despite storybook working fine. Is this due to how the Dropdown component is rendered?
                      <Fragment>
                        <Dropdown
                          onChange={handleVehicle}
                          options={myVehicles}
                          placeholder={t('selectVehicle', { ns: 'vehicle' })}
                        />
                      </Fragment>
                    )}
                    <Fragment>
                      <Dropdown
                        name="trimId"
                        placeholder={t('selectTrimPlaceholder', { ns: 'vehicle' })}
                        options={vehicleTrimOptions}
                        disabled={!vehicleTrimOptions.length}
                        onChange={handleTrimChange}
                        searchable
                      />
                      <Dropdown
                        id="fuelType"
                        name="fuel"
                        placeholder={t('fuelType', { ns: 'fuel' })}
                        onChange={handleFuelChange}
                        options={fuelOptions}
                        required
                        value={fuel}
                      />
                      <FlexGrid container className={cx('efficiencyGrid')}>
                        <FlexGrid.Column className={cx('efficiencyColumn')} mobile={4}>
                          <Label htmlFor="cityEfficiency">
                            {t('cityEfficiency', { ns: 'vehicle', unit: isMetric ? 'L/100km' : 'MPG' })}
                          </Label>
                          <Input
                            type="text"
                            id="cityEfficiency"
                            className={cx('efficiencyInput')}
                            onChange={handleCityEfficiency}
                            required
                            value={cityEfficiency}
                          />
                        </FlexGrid.Column>
                        <FlexGrid.Column className={cx('efficiencyColumn')} mobile={4}>
                          <Label htmlFor="highwayEfficiency">
                            {t('highwayEfficiency', { ns: 'vehicle', unit: isMetric ? 'L/100km' : 'MPG' })}
                          </Label>
                          <Input
                            type="text"
                            id="highwayEfficiency"
                            className={cx('efficiencyInput')}
                            onChange={handleHighwayEfficiency}
                            required
                            value={highwayEfficiency}
                          />
                        </FlexGrid.Column>
                        <FlexGrid.Column className={cx('efficiencyColumn')} mobile={4}>
                          <Label htmlFor="tankSize">
                            {t('tankSizeUnits', { ns: 'vehicle', unit: isMetric ? 'litres' : 'gallons' })}
                          </Label>
                          <Input
                            type="text"
                            id="tankSize"
                            className={cx('efficiencyInput')}
                            onChange={handleTankSize}
                            required
                            value={tankSize}
                          />
                        </FlexGrid.Column>
                      </FlexGrid>
                    </Fragment>
                    {!!isLoggedIn && !isInVehicleSelectMode && (
                      <Checkbox
                        id="saveVehicle"
                        name="saveVehicle"
                        label={t('saveToMyVehicles', { ns: 'vehicle' })}
                        onChange={handleSaveVehicleChange}
                        checked={shouldSaveVehicle}
                        role="checkbox"
                      />
                    )}
                    {!loading && displayError && (
                      <Message color="red">{displayError}</Message>
                    )}
                    {!loading && !displayError && displayWarning && (
                      <Message color="orange">{displayWarning}</Message>
                    )}
                    {loading && (
                      <Message color="green">
                        {t('tripCalculationInProgress', { ns: 'trip' })}
                      </Message>
                    )}
                    <Button disabled={!canSubmit} fluid loading={loading} primary type="submit">
                      {t('calculateTrip', { ns: 'trip' })}
                    </Button>
                  </Constraint>
                </LoadingMask>
              </Panel>
            </FlexGrid.Column>
          </FlexGrid>
        </Form>
        {bannerAds.map(({ id, path, sizes, sizeMapping }) => (
          <Fragment key={id}>
            <br />
            <AdContainer
              key={id}
              id={id}
              path={path}
              sizes={sizes}
              sizeMapping={sizeMapping}
              center
            />
          </Fragment>
        ))}
        {tripDetails && (
          <Fragment>
            <div className={cx('map')} ref={map}>
              <MapBoxMap
                accessToken={mapKey}
                fitBounds
                height={500}
                onLoad={scrollToMap}
                route={tripDetails.routeGeoCoords}
                zoom={8}
              >
                {routeMarkers}
              </MapBoxMap>
            </div>
            {hasSavedVehicle ? (
              <Fragment>
                {!isSavingVehicle && vehicleCreationError && (
                  <Fragment>
                    <br />
                    <Message color="red" dismissable>
                      {t('tripVehicleSaveError', { ns: 'trip' })}
                    </Message>
                    <br />
                  </Fragment>
                )}
                {!isSavingVehicle && !!vehicleCreationResult?.guid && (
                  <Fragment>
                    <br />
                    <Message color="green" dismissable>
                      <Trans i18nKey="vehicle:viewSavedVehicleProfile">
                        Vehicle Successfully Saved.&nbsp;
                        <Link to={`/account/vehicles/${vehicleCreationResult.guid}`} target="_blank">
                          View Vehicle Profile
                        </Link>
                      </Trans>
                    </Message>
                    <br />
                  </Fragment>
                )}
                {isSavingVehicle && (
                  <Fragment>
                    <br />
                    <Message color="green">
                      <Loader size="md">
                        {t('savingVehicle', { ns: 'vehicle' })}
                      </Loader>
                    </Message>
                    <br />
                  </Fragment>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <br /><br />
              </Fragment>
            )}
            <div className={cx('adRow')}>
              <Panel bordered className={cx('metrics')} color="white">
                <Header as="h2" size="sm">
                  {t('tripCostOverview', { ns: 'trip' })}
                </Header>
                <FlexGrid container>
                  <FlexGrid.Column className={cx('metric', 'blue')} tablet={3}>
                    <Text bold color="blue" fluid size="xl">{tripDetails.totalCost}</Text>
                    <Text as="p" className={cx('metricDescriptor')}>
                      {t('totalTripCost', { ns: 'trip', currency: summaryCurrency })}
                    </Text>
                  </FlexGrid.Column>
                  <FlexGrid.Column className={cx('metric')} tablet={3}>
                    <Text bold color="red" fluid size="xl">{tripDetails.savings}</Text>
                    <Text as="p" className={cx('metricDescriptor')}>
                      {t('yourSavings', { ns: 'trip', currency: summaryCurrency })}
                    </Text>
                  </FlexGrid.Column>
                  <FlexGrid.Column className={cx('metric')} tablet={3}>
                    <Text bold color="blue" fluid size="xl">{tripDetails.distance}</Text>
                    <Text as="p" className={cx('metricDescriptor')}>
                      {tripDetails.isMetric
                        ? t('tripDistanceKilometers', { ns: 'trip' })
                        : t('tripDistanceMiles', { ns: 'trip' })
                      }
                    </Text>
                  </FlexGrid.Column>
                  <FlexGrid.Column className={cx('metric')} tablet={3}>
                    <Text bold color="blue" fluid size="xl">{tripDetails.fuelQuantity}</Text>
                    <Text as="p" className={cx('metricDescriptor')}>
                      {tripDetails.isMetric
                        ? t('fuelUsedLitres', { ns: 'trip' })
                        : t('fuelUsedGallons', { ns: 'trip' })
                      }
                    </Text>
                  </FlexGrid.Column>
                </FlexGrid>
              </Panel>
              <Ad
                id={AD_SLOTS[19].slotID}
                path={AD_SLOTS[19].slotName}
                sizes={AD_SLOTS[19].sizes}
                sizeMapping={AD_SLOTS[19].sizeMapping}
                collapseIfEmpty
              />
            </div>
            <br /><br />
            <Header as="h2" className={cx('tripDetailsHeader')} size="sm">
              {t('tripDetails', { ns: 'trip' })}
            </Header>
          </Fragment>
        )}
      </Container>
      {tripDetails && (
        <Panel rounded={false} className={cx('tripDetailsPanel')}>
          <Container>
            <FlexGrid container>
              <FlexGrid.Column tablet={6}>
                {tripDetails.roundTrip && (
                  <Header as="h3" className={cx('returnTripHeader')}>
                    {t('toLocation', {
                      ns: 'trip',
                      location: tripDetails.outboundTripLegs[tripDetails.outboundTripLegs.length - 1].endWaypoint.displayName,
                    })}
                  </Header>
                )}
                {tripDetails.outboundTripLegs.map(leg => generateLegDetails(leg, tripDetails.roundTrip))}
              </FlexGrid.Column>
              {tripDetails.roundTrip && (
                <FlexGrid.Column className={cx('inboundLegs')} tablet={6}>
                  <Header as="h3" className={cx('returnTripHeader')}>
                    {t('returnTrip', { ns: 'trip' })}
                  </Header>
                  {tripDetails.inboundTripLegs.map(leg => generateLegDetails(leg, tripDetails.roundTrip))}
                </FlexGrid.Column>
              )}
            </FlexGrid>
          </Container>
        </Panel>
      )}
      <br />
      <Container>
        <Header as="h2" size="sm">
          {t('gasEstimatorHeader', { ns: 'trip' })}
        </Header>
        <Text as="p">
          {t('gasEstimatorDescription1', { ns: 'trip' })}
        </Text>
        <Text as="p">
          {t('gasEstimatorDescription2', { ns: 'trip' })}
        </Text>
        <br />
        <Header as="h2" size="sm">
          {t('beyondTCCHeader', { ns: 'trip' })}
        </Header>
        <Text as="p">
          {t('beyondTCCDescription', { ns: 'trip' })}
        </Text>
      </Container>
    </Page>
  );
}

TripCostCalculatorPage.propTypes = {
  origin: PropTypes.string,
  destinations: PropTypes.arrayOf(PropTypes.string),
};

TripCostCalculatorPage.defaultProps = {
  origin: undefined,
  destinations: undefined,
};
